"use client";

import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import type { MeasurementUnit } from "@/lib/types/measurements";
import { formatMeasurement, convertUnit } from "@/lib/types/measurements";

interface MeasurementValues {
  bustCm: number;
  waistCm: number;
  hipCm: number;
  shoulderCm: number;
  sleeveLengthCm: number;
  dressLengthCm: number;
}

interface MeasurementSummaryCardProps {
  measurements: MeasurementValues;
  versionNo?: number;
  capturedAt?: string;
  status?: string;
  className?: string;
  showHeader?: boolean;
  compact?: boolean;
}

const measurementLabels: Record<keyof MeasurementValues, string> = {
  bustCm: "Bust",
  waistCm: "Waist",
  hipCm: "Hip",
  shoulderCm: "Shoulder",
  sleeveLengthCm: "Sleeve Length",
  dressLengthCm: "Dress Length",
};

const statusColors: Record<string, string> = {
  DRAFT: "bg-gray-100 text-gray-700",
  SUBMITTED: "bg-blue-100 text-blue-700",
  PENDING_ACCEPTANCE: "bg-amber-100 text-amber-700",
  ACCEPTED_ACTIVE: "bg-emerald-100 text-emerald-700",
  REJECTED: "bg-red-100 text-red-700",
  SUPERSEDED: "bg-slate-100 text-slate-600",
};

export function MeasurementSummaryCard({
  measurements,
  versionNo,
  capturedAt,
  status,
  className,
  showHeader = true,
  compact = false,
}: MeasurementSummaryCardProps) {
  const [unit, setUnit] = useState<MeasurementUnit>("cm");

  const getValue = (key: keyof MeasurementValues): number => {
    const cmValue = measurements[key];
    return unit === "in" ? convertUnit(cmValue, "cm", "in") : cmValue;
  };

  const formatStatus = (s: string) => {
    return s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  };

  return (
    <Card className={cn("overflow-hidden", className)}>
      {showHeader && (
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CardTitle className="text-base">Measurements</CardTitle>
              {versionNo && (
                <Badge variant="outline" className="text-xs">
                  v{versionNo}
                </Badge>
              )}
              {status && (
                <Badge className={cn("text-xs font-medium", statusColors[status] || "bg-gray-100")}>
                  {formatStatus(status)}
                </Badge>
              )}
            </div>
            <div className="flex gap-1 bg-muted p-0.5 rounded-md">
              <Button
                variant={unit === "cm" ? "default" : "ghost"}
                size="sm"
                onClick={() => setUnit("cm")}
                className="h-6 px-2 text-xs"
              >
                CM
              </Button>
              <Button
                variant={unit === "in" ? "default" : "ghost"}
                size="sm"
                onClick={() => setUnit("in")}
                className="h-6 px-2 text-xs"
              >
                IN
              </Button>
            </div>
          </div>
          {capturedAt && (
            <p className="text-xs text-muted-foreground mt-1">
              Captured on {new Date(capturedAt).toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
              })}
            </p>
          )}
        </CardHeader>
      )}
      <CardContent className={cn(showHeader ? "pt-0" : "pt-4", compact ? "py-3" : "")}>
        {!showHeader && (
          <div className="flex justify-end mb-3">
            <div className="flex gap-1 bg-muted p-0.5 rounded-md">
              <Button
                variant={unit === "cm" ? "default" : "ghost"}
                size="sm"
                onClick={() => setUnit("cm")}
                className="h-6 px-2 text-xs"
              >
                CM
              </Button>
              <Button
                variant={unit === "in" ? "default" : "ghost"}
                size="sm"
                onClick={() => setUnit("in")}
                className="h-6 px-2 text-xs"
              >
                IN
              </Button>
            </div>
          </div>
        )}
        <div className={cn("grid gap-2", compact ? "grid-cols-3" : "grid-cols-2 sm:grid-cols-3")}>
          {(Object.keys(measurementLabels) as (keyof MeasurementValues)[]).map((key) => (
            <div
              key={key}
              className={cn(
                "flex flex-col p-2 rounded-lg bg-muted/50",
                compact ? "p-1.5" : "p-3"
              )}
            >
              <span className={cn("text-muted-foreground", compact ? "text-[10px]" : "text-xs")}>
                {measurementLabels[key]}
              </span>
              <span className={cn("font-semibold", compact ? "text-sm" : "text-base")}>
                {formatMeasurement(getValue(key), unit)}
              </span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
